import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Tooltip,
  Fade,
  Container,
  Paper,
  alpha,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ShoppingCart,
  Person,
  ExitToApp,
  Dashboard,
  ShoppingBag,
  Security,
  Email,
  Search,
  Notifications,
  Settings,
  Home,
  Store,
  Info,
  Code,
  Favorite,
  Support,
  VerifiedUser,
} from '@mui/icons-material';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { motion, useScroll, useTransform } from 'framer-motion';
import { logout } from '../../store/slices/authSlice';
import { toast } from 'react-toastify';
import { useNotifications } from '../../contexts/NotificationContext';
import '../../cosmic.css';

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const navbarRef = useRef(null);

  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { items } = useSelector((state) => state.cart);
  const { unreadCount } = useNotifications();

  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Scroll progress for navbar effects
  const { scrollYProgress } = useScroll();
  const navbarOpacity = useTransform(scrollYProgress, [0, 0.1], [0.95, 1]);
  const navbarBlur = useTransform(scrollYProgress, [0, 0.1], [20, 30]);
  const navbarY = useTransform(scrollYProgress, [0, 0.1], [0, -5]);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    // Always logout locally even if API call fails
    await dispatch(logout());
    handleMenuClose();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const menuItems = useMemo(() => [
    { text: 'Home', path: '/', icon: <Home /> },
    { text: 'Products', path: '/products', icon: <Store /> },
    { text: 'About', path: '/about', icon: <Info /> },
    { text: 'API Docs', path: '/api-docs', icon: <Code /> },
  ], []);

  const userMenuItems = useMemo(() => [
    { text: 'Profile', path: '/profile', icon: <Person />, description: 'Manage your account' },
    { text: 'Orders', path: '/orders', icon: <ShoppingBag />, description: 'View order history' },
    { text: 'Security', path: '/security', icon: <Security />, description: 'Account security' },
    { text: 'Dashboard', path: '/dashboard', icon: <Dashboard />, description: 'Analytics & insights' },
    { text: 'Favorites', path: '/favorites', icon: <Favorite />, description: 'Saved items' },
    { text: 'Support', path: '/support', icon: <Support />, description: 'Get help' },
  ], []);

  const isActiveRoute = (path) => location.pathname === path;

  const drawer = (
    <Box sx={{ width: 280, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{
        p: 3,
        textAlign: 'center',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`
      }}>
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <Email sx={{ fontSize: 28, color: theme.palette.primary.main, mr: 1 }} />
            <Typography variant="h6" sx={{
              fontWeight: 600,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              Secure E-Commerce
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            Professional Digital Solutions
          </Typography>
        </motion.div>
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List sx={{ pt: 1 }}>
          {menuItems.map((item, index) => (
            <motion.div
              key={item.text}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: index * 0.08, duration: 0.3 }}
            >
              <ListItem
                component={Link}
                to={item.path}
                onClick={handleDrawerToggle}
                sx={{
                  mx: 1.5,
                  mb: 0.5,
                  borderRadius: 2,
                  backgroundColor: isActiveRoute(item.path) ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                  border: isActiveRoute(item.path) ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}` : '1px solid transparent',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    transform: 'translateX(4px)',
                  },
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <ListItemIcon sx={{
                  minWidth: 36,
                  color: isActiveRoute(item.path) ? theme.palette.primary.main : 'text.secondary'
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isActiveRoute(item.path) ? 600 : 500,
                    color: isActiveRoute(item.path) ? 'primary.main' : 'text.primary',
                    fontSize: '0.9rem'
                  }}
                />
              </ListItem>
            </motion.div>
          ))}

          {isAuthenticated && (
            <>
              <Divider sx={{ my: 2, mx: 1.5 }} />
              <Typography variant="overline" sx={{ px: 2.5, py: 1, color: 'text.secondary', fontWeight: 600, fontSize: '0.75rem' }}>
                Account
              </Typography>
              {userMenuItems.map((item, index) => (
                <motion.div
                  key={item.text}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: (index + menuItems.length) * 0.08, duration: 0.3 }}
                >
                  <ListItem
                    component={Link}
                    to={item.path}
                    onClick={handleDrawerToggle}
                    sx={{
                      mx: 1.5,
                      mb: 0.5,
                      borderRadius: 2,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        transform: 'translateX(4px)',
                      },
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      secondary={item.description}
                      primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                  </ListItem>
                </motion.div>
              ))}
            </>
          )}
        </List>
      </Box>

      {/* Footer */}
      {isAuthenticated && (
        <Box sx={{ p: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
          <Paper sx={{
            p: 2,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.secondary.main, 0.06)} 100%)`,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <Avatar sx={{
                width: 32,
                height: 32,
                mr: 2,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                fontSize: '0.875rem',
                fontWeight: 600
              }}>
                {user?.first_name?.[0] || user?.email?.[0] || 'U'}
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem' }}>
                  {user?.first_name || user?.email}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  {user?.email}
                </Typography>
              </Box>
            </Box>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              onClick={handleLogout}
              startIcon={<ExitToApp />}
              sx={{
                mt: 1,
                fontSize: '0.8rem',
                py: 0.75,
                borderColor: alpha(theme.palette.primary.main, 0.3),
                color: theme.palette.primary.main,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.08)
                }
              }}
            >
              Sign Out
            </Button>
          </Paper>
        </Box>
      )}
    </Box>
  );

  return (
    <>


      <motion.div
        ref={navbarRef}
        style={{
          opacity: navbarOpacity,
          backdropFilter: `blur(${navbarBlur}px)`,
          y: navbarY,
          willChange: 'opacity, transform, backdrop-filter',
        }}
      >
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            background: scrolled
              ? `linear-gradient(135deg, rgba(8, 8, 16, 0.95) 0%, rgba(16, 12, 28, 0.95) 25%, rgba(24, 18, 40, 0.95) 50%, rgba(16, 12, 28, 0.95) 75%, rgba(8, 8, 16, 0.95) 100%)`
              : `linear-gradient(135deg, rgba(8, 8, 16, 0.85) 0%, rgba(16, 12, 28, 0.85) 25%, rgba(24, 18, 40, 0.85) 50%, rgba(16, 12, 28, 0.85) 75%, rgba(8, 8, 16, 0.85) 100%)`,
            backdropFilter: scrolled ? 'blur(40px) saturate(1.3)' : 'blur(30px) saturate(1.2)',
            borderBottom: scrolled
              ? `1px solid ${alpha(theme.palette.primary.main, 0.4)}`
              : `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            boxShadow: scrolled
              ? `0 16px 50px ${alpha(theme.palette.primary.main, 0.25)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}, inset 0 1px 0 ${alpha(theme.palette.primary.main, 0.15)}`
              : `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)}, inset 0 1px 0 ${alpha(theme.palette.primary.main, 0.08)}`,
            transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                radial-gradient(circle at 20% 50%, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 50%),
                radial-gradient(circle at 80% 50%, ${alpha(theme.palette.secondary.main, 0.06)} 0%, transparent 50%),
                linear-gradient(135deg, transparent 0%, ${alpha(theme.palette.primary.main, 0.02)} 50%, transparent 100%)
              `,
              opacity: scrolled ? 0.8 : 0.6,
              pointerEvents: 'none',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: `linear-gradient(90deg, transparent 0%, ${alpha(theme.palette.primary.main, 0.6)} 25%, ${alpha(theme.palette.secondary.main, 0.6)} 50%, ${alpha(theme.palette.primary.main, 0.6)} 75%, transparent 100%)`,
              opacity: scrolled ? 1 : 0.7,
              transition: 'opacity 0.4s ease',
              boxShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.3)}`,
            },
          }}
        >
          <Container maxWidth="xl">
            <Toolbar sx={{ px: { xs: 1, sm: 2 }, py: 1.5, minHeight: 64 }}>
              {/* Mobile menu button */}
              {isMobile && (
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="start"
                    onClick={handleDrawerToggle}
                    sx={{
                      mr: 2,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.15),
                        boxShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <MenuIcon sx={{
                      filter: `drop-shadow(0 0 8px ${alpha(theme.palette.primary.main, 0.5)})`,
                    }} />
                  </IconButton>
                </motion.div>
              )}

              {/* Logo */}
              <div>
                <Typography
                  variant="h6"
                  component={Link}
                  to="/"
                  sx={{
                    textDecoration: 'none',
                    color: 'inherit',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.primary.main} 100%)`,
                    backgroundSize: '200% 200%',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontSize: { xs: '1.2rem', sm: '1.4rem' },
                    letterSpacing: '-0.03em',
                    textShadow: `0 0 30px ${alpha(theme.palette.primary.main, 0.4)}`,
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '120%',
                      height: '120%',
                      background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
                      borderRadius: '50%',
                      zIndex: -1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: { xs: 32, sm: 36 },
                      height: { xs: 32, sm: 36 },
                      borderRadius: '12px',
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.2)} 100%)`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.2)}, inset 0 1px 0 ${alpha(theme.palette.primary.main, 0.1)}`,
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <Email sx={{
                      fontSize: { xs: 18, sm: 20 },
                      color: theme.palette.primary.main,
                      filter: `drop-shadow(0 0 8px ${alpha(theme.palette.primary.main, 0.5)})`,
                    }} />
                  </Box>
                  <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                    Secure E-Commerce
                  </Box>
                  <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                    SEC
                  </Box>
                </Typography>
              </div>

              <Box sx={{ flexGrow: 1 }} />

              {/* Desktop Navigation */}
              {!isMobile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {menuItems.map((item, index) => (
                    <div
                      key={item.text}
                    >
                      <Button
                        color="inherit"
                        component={Link}
                        to={item.path}
                        startIcon={item.icon}
                        sx={{
                          position: 'relative',
                          borderRadius: 3,
                          px: 3,
                          py: 1.2,
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          backgroundColor: isActiveRoute(item.path)
                            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.15)} 100%)`
                            : 'transparent',
                          border: isActiveRoute(item.path)
                            ? `1px solid ${alpha(theme.palette.primary.main, 0.5)}`
                            : `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                          backdropFilter: 'blur(15px)',
                          boxShadow: isActiveRoute(item.path)
                            ? `0 8px 25px ${alpha(theme.palette.primary.main, 0.25)}, inset 0 1px 0 ${alpha(theme.palette.primary.main, 0.2)}`
                            : 'none',
                          '&:hover': {
                            backgroundColor: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                            boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.2)}, inset 0 1px 0 ${alpha(theme.palette.primary.main, 0.15)}`,
                            transform: 'translateY(-1px)',
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            borderRadius: 3,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                          },
                          '&:hover::before': {
                            opacity: 1,
                          },
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: '50%',
                            width: isActiveRoute(item.path) ? '80%' : '0%',
                            height: 3,
                            background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.primary.main} 100%)`,
                            transform: 'translateX(-50%)',
                            transition: 'width 0.4s ease',
                            borderRadius: 2,
                            boxShadow: `0 0 15px ${alpha(theme.palette.primary.main, 0.6)}`,
                          },
                          '&:hover::after': {
                            width: '80%',
                          },
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        {item.text}
                      </Button>
                    </div>
                  ))}
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 2 }}>
                {/* Search Button */}
                <Tooltip title="Search" arrow>
                  <IconButton
                    color="inherit"
                    sx={{
                      borderRadius: 2,
                      width: 40,
                      height: 40,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        backgroundColor: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                        boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.2)}, inset 0 1px 0 ${alpha(theme.palette.primary.main, 0.1)}`,
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    <Search />
                  </IconButton>
                </Tooltip>

                {/* Notifications */}
                {isAuthenticated && (
                  <Tooltip title="Notifications" arrow>
                    <IconButton
                      color="inherit"
                      onClick={() => {
                        navigate('/notifications');
                      }}
                      sx={{
                        borderRadius: 2,
                        width: 40,
                        height: 40,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        backdropFilter: 'blur(10px)',
                        '&:hover': {
                          backgroundColor: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                          boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.2)}, inset 0 1px 0 ${alpha(theme.palette.primary.main, 0.1)}`,
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      <Badge badgeContent={unreadCount} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem', height: 18, minWidth: 18 } }}>
                        <Notifications />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                )}

                {/* Cart */}
                {isAuthenticated && (
                  <Tooltip title="Shopping Cart" arrow>
                    <IconButton
                      color="inherit"
                      component={Link}
                      to="/cart"
                      sx={{
                        borderRadius: 2,
                        width: 40,
                        height: 40,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        backdropFilter: 'blur(10px)',
                        '&:hover': {
                          backgroundColor: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                          boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.2)}, inset 0 1px 0 ${alpha(theme.palette.primary.main, 0.1)}`,
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      <Badge badgeContent={items.length} color="secondary" sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem', height: 18, minWidth: 18 } }}>
                        <ShoppingCart />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                )}

                {/* User Menu */}
                {isAuthenticated ? (
                  <>
                    <IconButton
                      onClick={handleProfileMenuOpen}
                      sx={{
                        borderRadius: 2,
                        width: 44,
                        height: 44,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        '&:hover': {
                          backgroundColor: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                          boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.25)}, inset 0 1px 0 ${alpha(theme.palette.primary.main, 0.15)}`,
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 38,
                          height: 38,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.primary.main} 100%)`,
                          border: `2px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                          fontSize: '0.9rem',
                          fontWeight: 700,
                          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}, inset 0 1px 0 ${alpha(theme.palette.primary.main, 0.2)}`,
                          backdropFilter: 'blur(5px)',
                        }}
                      >
                        {user?.first_name?.[0] || user?.email?.[0] || 'U'}
                      </Avatar>
                    </IconButton>

                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                      TransitionComponent={Fade}
                      PaperProps={{
                        sx: {
                          mt: 1,
                          minWidth: 260,
                          borderRadius: 2,
                          background: `linear-gradient(135deg, rgba(10, 10, 20, 0.98) 0%, rgba(20, 15, 35, 0.98) 100%)`,
                          backdropFilter: 'blur(25px)',
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.1)}`,
                        },
                      }}
                    >
                      {/* User Info */}
                      <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 44,
                              height: 44,
                              mr: 2,
                              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                              fontSize: '1rem',
                              fontWeight: 600,
                              boxShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                            }}
                          >
                            {user?.first_name?.[0] || user?.email?.[0] || 'U'}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.95rem' }}>
                              {user?.first_name || user?.email}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                              {user?.email}
                            </Typography>
                          </Box>
                        </Box>
                        <Chip
                          icon={<VerifiedUser sx={{ fontSize: '0.875rem' }} />}
                          label="Premium Member"
                          size="small"
                          sx={{
                            backgroundColor: theme.palette.secondary.main,
                            color: 'white',
                            fontSize: '0.7rem',
                            height: 20,
                            '& .MuiChip-label': { px: 1 },
                            boxShadow: `0 0 10px ${alpha(theme.palette.secondary.main, 0.3)}`,
                          }}
                        />
                      </Box>

                      {/* Menu Items */}
                      {userMenuItems.map((item) => (
                        <MenuItem
                          key={item.text}
                          component={Link}
                          to={item.path}
                          onClick={handleMenuClose}
                          sx={{
                            px: 3,
                            py: 1.5,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.08),
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                            {item.icon}
                          </ListItemIcon>
                          <Box>
                            <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.875rem' }}>
                              {item.text}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              {item.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}

                      <Divider sx={{ my: 1 }} />

                      {/* Settings & Logout */}
                      <MenuItem sx={{ px: 3, py: 1.5 }}>
                        <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                          <Settings />
                        </ListItemIcon>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>Settings</Typography>
                      </MenuItem>

                      <MenuItem
                        onClick={handleLogout}
                        sx={{
                          px: 3,
                          py: 1.5,
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.error.main, 0.08),
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: theme.palette.error.main }}>
                          <ExitToApp />
                        </ListItemIcon>
                        <Typography variant="body2" color="error.main" sx={{ fontSize: '0.875rem' }}>
                          Sign Out
                        </Typography>
                      </MenuItem>
                    </Menu>
                  </>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button
                        color="inherit"
                        component={Link}
                        to="/login"
                        sx={{
                          borderRadius: 2,
                          px: 2.5,
                          py: 1,
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                          backdropFilter: 'blur(10px)',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.12),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.2)}`,
                            transform: 'translateY(-1px)',
                          },
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        Sign In
                      </Button>
                    </motion.div>
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button
                        variant="contained"
                        component={Link}
                        to="/register"
                        sx={{
                          borderRadius: 2,
                          px: 2.5,
                          py: 1,
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                          '&:hover': {
                            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.4)}`,
                          },
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        Get Started
                      </Button>
                    </motion.div>
                  </Box>
                )}
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      </motion.div>

      {/* Spacer for fixed navbar */}
      <Box sx={{ height: 64 }} />

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            background: `linear-gradient(135deg, rgba(10, 10, 20, 0.98) 0%, rgba(20, 15, 35, 0.98) 100%)`,
            backdropFilter: 'blur(25px)',
            borderRight: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar;
